import type React from "react";
import { Fragment, useMemo, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp, MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import { styled } from "@mui/material/styles";

import { type CASE_SHARE_FILTER, type CaseBase, CaseCopyStatus, CaseStatus } from "src/types";
import { useCaseDeleteMutation } from "src/hooks";
import { isDisabled, isEnabled } from "src/utils/permission.utils";
import { match, parse } from "../utils/autosuggest-highlight.utils";
import { CaseStatusChip } from "src/components/CaseStatusChip";
import { CaseErrorsDialog } from "src/components/CaseErrorsDialog";
import { StickyTable } from "src/components/StickyTable";
import { IsVisible } from "src/components/permissions";
import { TableHeadCell } from "src/components/TableHeadCell";
import { CaseCopyDialog } from "src/components/CaseCopyDialog";
import { CaseCopyStatusChip } from "src/components/CaseCopyStatusChip";
import { CaseOverviewDetailsTable } from "src/CaseOverviewDetailsTable";
import { CaseShareDialog } from "src/components/CaseShareDialog";
import { ShareIcon } from "src/components/icons/ShareIcon";
import { UsersIcon } from "src/components/icons/UsersIcon";
import { useCaseShareRevoke } from "src/hooks/caseHooks";
import { useSnackbar } from "notistack";

import { parseServerError } from "src/utils/string.utils";

interface Props {
  className?: string;
  data: CaseBase[];
  searchQuery: string;
  caseShareFilter: CASE_SHARE_FILTER;
}

export type OrderColumn = "name" | "dateCreated" | "status" | "runYear" | "models";
export type OrderDirection = "asc" | "desc";

export function CaseOverviewTable(props: Props) {
  const { t } = useTranslation();
  const { data: cases } = props;
  /**
   * Local state
   */
  const [orderBy, setOrderBy] = useState<OrderColumn>("dateCreated");
  const [orderDirection, setOrderDirection] = useState<OrderDirection>("desc");
  const [shareDialogCase, setShareDialogCase] = useState<CaseBase | null>(null);
  /**
   * Memo
   */
  const visualData = useMemo(() => {
    const filteredData = filterByQuery(cases, props.searchQuery, props.caseShareFilter);
    const sortedData = sortData(filteredData, orderBy, orderDirection);
    return sortedData;
  }, [cases, orderBy, orderDirection, props.searchQuery, props.caseShareFilter]);

  /**
   * Functions
   */
  function handleSort(column: OrderColumn) {
    if (column !== orderBy) {
      setOrderBy(column);
      setOrderDirection("asc");
    }
    if (column === orderBy) {
      toggleOrderDirection();
    }
  }

  function toggleOrderDirection() {
    if (orderDirection === "asc") {
      setOrderDirection("desc");
    } else if (orderDirection === "desc") {
      setOrderDirection("asc");
    }
  }
  return render();

  function render() {
    return (
      <>
        <StyledStickyTable className={props.className}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableHeadCell<OrderColumn>
                dataKey="name"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={() => handleSort("name")}
              >
                {t("label.caseName")}
              </TableHeadCell>
              <TableHeadCell<OrderColumn>
                dataKey="dateCreated"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={() => handleSort("dateCreated")}
              >
                {t("label.created")}
              </TableHeadCell>
              <TableHeadCell<OrderColumn>
                dataKey="runYear"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={() => handleSort("runYear")}
              >
                {t("label.runYear")}
              </TableHeadCell>
              <TableHeadCell<OrderColumn>
                dataKey="models"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={() => handleSort("models")}
              >
                {t("label.models")}
              </TableHeadCell>

              <TableHeadCell<OrderColumn>
                dataKey="status"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={() => handleSort("status")}
              >
                {t("label.status")}
              </TableHeadCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visualData.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("label.noResultsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {visualData.map((item, index) => (
              <Row
                key={item.id}
                data={item}
                className={index % 2 === 0 ? "even" : "odd"}
                searchQuery={props.searchQuery}
                onOpenShareDialog={setShareDialogCase}
              />
            ))}
          </TableBody>
        </StyledStickyTable>
        {shareDialogCase && (
          <CaseShareDialog open={true} case={shareDialogCase} onClose={() => setShareDialogCase(null)} />
        )}
      </>
    );
  }
}

function Row(props: {
  data: CaseBase;
  searchQuery: string;
  className?: string;
  onOpenShareDialog: (c: CaseBase) => void;
}) {
  const { data } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showErrors, setShowErrors] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [openCaseCopyDialog, setOpenCaseCopyDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const deleteMutation = useCaseDeleteMutation(data.id);
  const { mutate: revokeCase, isLoading: isRevoking } = useCaseShareRevoke(data.id);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }

  function handleLeaveSharedCase() {
    revokeCase(
      { leaving: true },
      {
        onSuccess: () => {
          handleClose();
        },
        onError: (e) => {
          const error = e.response?.data[0];
          if (error) {
            enqueueSnackbar(parseServerError(error), {
              variant: "error",
            });
          } else {
            enqueueSnackbar(t("label.somethingWentWrong"), {
              variant: "error",
            });
          }
        },
      }
    );
  }

  /**
   * Functions
   */
  function handleOpenCase() {
    if (isEnabled(data.permissions.update)) {
      navigate(`/cases/${data.id}`);
    }
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleDelete() {
    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteCase", { name: data.name }),
    })
      .then(() => deleteMutation.mutate())
      .catch(() => {
        // User cancelled - do nothing
      })
      .finally(() => handleClose());
  }

  return render();

  function render() {
    const matches = match(data.name, props.searchQuery, true);
    const parts = parse(data.name, matches);
    const showCopyStatus = data.copyStatus === CaseCopyStatus.COPYING || data.copyStatus === CaseCopyStatus.FAILED;

    return (
      <Fragment key={data.id}>
        <TableRow className={`${props.className} ${showDetails ? "details-open" : ""}`.trim()}>
          <TableCell className="fixed-width">
            <IconButton onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell scope="row" className="case-name-cell">
            <div className="case-name-container">
              <div className="case-name-row">
                {data.sharedCase && (
                  <Tooltip title={t("label.sharedWithYou")}>
                    <span className="shared-icon-wrap">
                      <UsersIcon className="shared-users-icon" />
                    </span>
                  </Tooltip>
                )}
                {!data.sharedCase && data.sharedEmailList.length > 0 && (
                  <Tooltip title={t("label.youAreSharingThisCaseWithOthers")}>
                    <span className="shared-icon-wrap">
                      <ShareIcon className="shared-share-icon" />
                    </span>
                  </Tooltip>
                )}
                <Button
                  variant="text"
                  className={`name ${isEnabled(data.permissions.update) ? "editable" : ""}`.trim()}
                  disabled={isDisabled(data.permissions.update)}
                  onClick={handleOpenCase}
                >
                  {parts.map((part, index) => (
                    <span key={index} className={`part ${part.highlight ? "highlight" : ""}`.trim()}>
                      {part.text}
                    </span>
                  ))}
                </Button>
              </div>
              <span className="scenarios">
                {t("label.scenariosConfigured", { count: Math.max(0, data.nbScenarios - 1) })}
              </span>
            </div>
          </TableCell>
          <TableCell>{t("label.dateCreated", { date: data.dateCreated })}</TableCell>
          <TableCell>{data.runYear}</TableCell>
          <TableCell>{data.models}</TableCell>
          <TableCell>
            {showCopyStatus && <CaseCopyStatusChip label={t(`label.${data.copyStatus}`)} status={data.copyStatus} />}
            {!showCopyStatus && <CaseStatusChip label={t(`label.caseStatus.${data.status}`)} status={data.status} />}
          </TableCell>

          <TableCell align="right" className="actions-cell">
            {renderRowActions()}
          </TableCell>
        </TableRow>
        {showDetails && <CaseOverviewDetailsTable case={data} className={props.className} />}
      </Fragment>
    );
  }

  function renderRowActions() {
    return (
      <>
        {data.status === CaseStatus.CALCULATION_FINISHED && (
          <Button variant="text" color="primary" component={NavLink} to={`/cases/${data.id}/results`}>
            {t("label.viewResults")}
          </Button>
        )}
        {(data.status === CaseStatus.CREATION_FAILED ||
          data.status === CaseStatus.CALCULATION_FAILED ||
          data.copyStatus === CaseCopyStatus.FAILED) && (
          <>
            <Button className="btn-error" variant="text" onClick={() => setShowErrors(true)}>
              {t("label.errorDetails")}
            </Button>
            <CaseErrorsDialog open={showErrors} case={data} onClose={() => setShowErrors(false)} />
          </>
        )}
        <IconButton onClick={handleClick} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()} disablePortal>
          <MenuItem disabled={isDisabled(data.permissions.update)} onClick={handleOpenCase}>
            {t("label.edit")}
          </MenuItem>
          <MenuItem
            disabled={isDisabled(data.permissions.update)}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setOpenCaseCopyDialog(true);
            }}
          >
            {t("label.copy")}
          </MenuItem>
          <IsVisible permission={data.permissions.delete}>
            <MenuItem
              disabled={isDisabled(data.permissions.delete)}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete();
              }}
            >
              {t("label.delete")}
            </MenuItem>
          </IsVisible>
          <Tooltip
            disableInteractive
            title={data.sharedCase ? t("label.onlyOwnerCanShareThisCase") : t("label.onlyCalculatedCasesCanBeShared")}
            disableHoverListener={!isDisabled(data.permissions.share)}
          >
            <span>
              <MenuItem
                disabled={isDisabled(data.permissions.share)}
                onClick={(event) => {
                  event.stopPropagation();
                  handleClose();
                  props.onOpenShareDialog(data);
                }}
              >
                {t("label.share")}
              </MenuItem>
            </span>
          </Tooltip>
          {data.sharedCase && (
            <MenuItem
              disabled={isRevoking}
              onClick={(event) => {
                event.stopPropagation();
                handleLeaveSharedCase();
              }}
              className={isRevoking ? "loading" : ""}
            >
              {isRevoking && <CircularProgress size={16} />}

              {t("label.leaveSharedCase")}
            </MenuItem>
          )}
        </Menu>

        <CaseCopyDialog onClose={() => setOpenCaseCopyDialog(false)} caseId={data.id} open={openCaseCopyDialog} />
      </>
    );
  }
}

const StyledStickyTable = styled(StickyTable)`
  .fixed-width {
    width: 70px;
  }
  .MuiMenuItem-root {
    &.loading.Mui-disabled {
      color: transparent;
      .MuiCircularProgress-root {
        opacity: 1;
        position: absolute;
      }
    }
  }
  .details-open {
    .MuiTableCell-root {
      border-bottom: unset;
    }
  }
  .actions-cell {
    white-space: nowrap;
  }
  .MuiTableHead-root {
    white-space: nowrap;
  }
  .case-name-container {
    display: flex;
    flex-direction: column;
  }
  .case-name-row {
    display: flex;
    align-items: center;
    gap: 9px;

    .shared-icon-wrap {
      display: inline-flex;
      align-items: center;
    }

    .MuiSvgIcon-root {
      font-size: 14px;
      transform: translateY(-1px);
    }
  }
  .case-name-cell {
    .name {
      text-transform: none;
      justify-content: flex-start;
      text-align: left;
      margin-left: ${({ theme }) => theme.spacing(-1)};
      color: ${({ theme }) => theme.palette.text.secondary};

      &.editable {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    }

    .part {
      font-weight: 500;

      &.highlight {
        font-weight: 900;
      }
    }

    .scenarios {
      font-style: italic;
      font-size: 11px;
      font-weight: 400;
    }
  }

  .btn-error {
    color: ${({ theme }) => theme.palette.error.main};
  }

  .shared-users-icon {
    color: #9d63d8;
  }
  .shared-share-icon {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

function sortData(data: CaseBase[], column: OrderColumn, direction: OrderDirection) {
  if (column === "dateCreated") {
    return sortByDate(data, column, direction);
  }
  return sortByString(data, column, direction);
}

function sortByDate(data: CaseBase[], column: OrderColumn, direction: OrderDirection) {
  return [...data].sort((a, b) => {
    const nameA = (a[column] as Date).getTime(); // ignore upper and lowercase
    const nameB = (b[column] as Date).getTime(); // ignore upper and lowercase
    if (direction === "asc") {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    }
    if (direction === "desc") {
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;
    }

    // names must be equal
    return 0;
  });
}

function sortByString(data: CaseBase[], column: OrderColumn, direction: OrderDirection) {
  return [...data].sort((a, b) => {
    const nameA = a[column].toString().toUpperCase(); // ignore upper and lowercase
    const nameB = b[column].toString().toUpperCase(); // ignore upper and lowercase
    if (direction === "asc") {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    }
    if (direction === "desc") {
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;
    }

    // names must be equal
    return 0;
  });
}

function matchesShareFilter(acase: CaseBase, filter: CASE_SHARE_FILTER): boolean {
  switch (filter) {
    case "ALL":
      return true;
    case "SHARED_WITH_ME":
      return acase.sharedCase === true;
    case "SHARED_BY_ME":
      return acase.sharedEmailList.length > 0;
    default:
      return false;
  }
}

function filterByQuery(cases: CaseBase[], query: string, caseShareFilter: CASE_SHARE_FILTER) {
  return cases.filter((acase) => {
    const matchesName = acase.name.toLowerCase().includes(query.toLowerCase());
    return matchesName && matchesShareFilter(acase, caseShareFilter);
  });
}
